import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Dialog, 
    DialogContent,
    Typography, 
    Autocomplete, 
    TextField, 
    Button,
    CircularProgress,
    Stack
} from '@mui/material';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { useNavigate } from 'react-router-dom';
import { eraseCookie } from '../../../infrastructure/utils/cookieStorage';
import { locationService } from '../../../infrastructure/services/locationService';

export default function ProfileCompletionModal({
    open,
    isSaving,
    onSave
}) {
    const navigate = useNavigate();

    const handleLogout = () => {
        eraseCookie('authToken');
        eraseCookie('student_auth_token');
        eraseCookie('student_user');
        eraseCookie('studentId');
        eraseCookie('userPoints');
        navigate('/');
    };
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedProvinceObj, setSelectedProvinceObj] = useState(null);
    const [selectedDistrictObj, setSelectedDistrictObj] = useState(null);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    useEffect(() => {
        if (open) {
            const fetchProvinces = async () => {
                setLoadingProvinces(true);
                try {
                    const data = await locationService.getProvinces();
                    setProvinces(data || []);
                } catch (e) {
                    console.error('Failed to load provinces:', e);
                } finally {
                    setLoadingProvinces(false);
                }
            };
            fetchProvinces();
        }
    }, [open]);

    useEffect(() => {
        if (selectedProvinceObj) {
            const fetchDistricts = async () => {
                setLoadingDistricts(true);
                try {
                    const provId = selectedProvinceObj.index ?? selectedProvinceObj.id;
                    const data = await locationService.getDistrictsByProvince(provId);
                    setDistricts(data || []);
                } catch (e) {
                    console.error('Failed to load districts:', e);
                } finally {
                    setLoadingDistricts(false);
                }
            };
            fetchDistricts();
        } else {
            setDistricts([]);
            setSelectedDistrictObj(null);
        }
    }, [selectedProvinceObj]);

    const editableFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            height: '48px',
            fontFamily: 'Poppins',
            fontSize: '14px',
            color: '#1e293b',
            fontWeight: 500,
            backgroundColor: '#ffffff',
            '& fieldset': {
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
            },
            '&:hover fieldset': {
                borderColor: '#cbd5e1'
            },
            '&.Mui-focused fieldset': {
                borderColor: '#ff502d'
            }
        },
        '& .MuiOutlinedInput-input': {
            '&::placeholder': {
                color: '#94a3b8',
                opacity: 1
            }
        }
    };

    const SectionLabel = ({ children }) => (
        <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#1e293b', mb: 1, mt: 2, fontFamily: 'Poppins', textAlign: 'left' }}>
            {children}
        </Typography>
    );

    const handleSave = () => {
        if (selectedProvinceObj && selectedDistrictObj) {
            onSave(
                selectedProvinceObj.en_US || selectedProvinceObj.name || '', 
                selectedDistrictObj.en_US || selectedDistrictObj.name || ''
            );
        }
    };

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(8px)',
                    }
                }
            }}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '28px',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogContent sx={{ p: 4, pt: 4, pb: 4, textAlign: 'center' }}>
                <ErrorRoundedIcon sx={{ fontSize: 56, color: '#ff3b30', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', fontFamily: 'Poppins', mb: 1 }}>
                    Complete Your Profile
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins', mb: 3, lineHeight: 1.5 }}>
                    කරුණාකර ඔබගේ පළාත සහ දිස්ත්‍රික්කය තෝරන්න.මෙය ඔබගේ District, Island Rank එක බලාගැනීමට ප්‍රයෝජනවත් වේ.
                </Typography>

                <Box sx={{ width: '100%', mt: 1 }}>
                    <SectionLabel>Your Province (ඔබගේ පළාත)</SectionLabel>
                    <Autocomplete
                        options={provinces}
                        getOptionLabel={(option) => {
                            if (option.en_US && option.si_LK) {
                                return `${option.en_US} (${option.si_LK})`;
                            }
                            return option.en_US || option.name || '';
                        }}
                        isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                        value={selectedProvinceObj}
                        onChange={(_, newVal) => {
                            setSelectedProvinceObj(newVal);
                            setSelectedDistrictObj(null);
                        }}
                        loading={loadingProvinces}
                        disableClearable={!selectedProvinceObj}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select your province"
                                sx={editableFieldSx}
                                InputProps={{
                                    ...(params.InputProps || {}),
                                    endAdornment: (
                                        <>
                                            {loadingProvinces ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps?.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: '16px',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                                    '& .MuiAutocomplete-listbox': {
                                        p: '6px',
                                        maxHeight: '200px',
                                        '& .MuiAutocomplete-option': {
                                            borderRadius: '10px',
                                            fontFamily: 'Poppins',
                                            fontSize: '13.5px',
                                            px: 2,
                                            py: '10px',
                                        }
                                    }
                                }
                            }
                        }}
                    />

                    <SectionLabel>Your District (ඔබගේ දිස්ත්‍රික්කය)</SectionLabel>
                    <Autocomplete
                        options={districts}
                        getOptionLabel={(option) => {
                            if (option.en_US && option.si_LK) {
                                return `${option.en_US} (${option.si_LK})`;
                            }
                            return option.en_US || option.name || '';
                        }}
                        isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                        disabled={!selectedProvinceObj}
                        value={selectedDistrictObj}
                        onChange={(_, newVal) => setSelectedDistrictObj(newVal)}
                        loading={loadingDistricts}
                        disableClearable={!selectedDistrictObj}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selectedProvinceObj ? (loadingDistricts ? "Loading districts..." : "Select your district") : "Select province first"}
                                sx={editableFieldSx}
                                InputProps={{
                                    ...(params.InputProps || {}),
                                    endAdornment: (
                                        <>
                                            {loadingDistricts ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps?.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: '16px',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                                    '& .MuiAutocomplete-listbox': {
                                        p: '6px',
                                        maxHeight: '200px',
                                        '& .MuiAutocomplete-option': {
                                            borderRadius: '10px',
                                            fontFamily: 'Poppins',
                                            fontSize: '13.5px',
                                            px: 2,
                                            py: '10px',
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </Box>

                <Stack direction="row" spacing={2} sx={{ mt: 4, width: '100%' }}>
                    <Button
                        variant="outlined"
                        onClick={handleLogout}
                        sx={{
                            flex: 1,
                            height: '48px',
                            borderRadius: '24px',
                            borderColor: '#dc2626',
                            color: '#dc2626',
                            borderWidth: '1.5px',
                            fontWeight: 700,
                            fontSize: '15px',
                            fontFamily: 'Poppins',
                            textTransform: 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                borderColor: '#b91c1c',
                                bgcolor: 'rgba(220, 38, 38, 0.05)',
                                borderWidth: '1.5px'
                            }
                        }}
                    >
                        Logout
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || !selectedProvinceObj || !selectedDistrictObj}
                        sx={{
                            flex: 1,
                            height: '48px',
                            borderRadius: '24px',
                            background: 'linear-gradient(135deg, #ff7e40 0%, #ff502d 100%)',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '15px',
                            fontFamily: 'Poppins',
                            boxShadow: '0 8px 20px rgba(255, 80, 45, 0.25)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #ff8e54 0%, #ff613f 100%)',
                                boxShadow: '0 10px 24px rgba(255, 80, 45, 0.35)',
                                transform: 'translateY(-1px)'
                            },
                            '&:disabled': {
                                background: '#cbd5e1',
                                color: '#94a3b8',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {isSaving ? (
                            <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
